import { supabase } from '@/integrations/supabase/client';
import { ApiResult, ok, err, ApiError } from '../errors';
import type { User, Session } from '@supabase/supabase-js';

// ============ Types ============

export interface AuthState {
  user: User | null;
  session: Session | null;
}

export interface SignUpValidation {
  allowed: boolean;
  method: string | null;
  group_id: string | null;
  subgroup_id: string | null;
  allowed_products: string[] | null;
}

export interface EnrollmentWithProduct {
  product_id: string;
  is_active: boolean | null;
  product_slug: string;
  product_name: string;
}

// ============ User Enrollments ============

export async function getUserEnrollments(userId: string): Promise<ApiResult<EnrollmentWithProduct[]>> {
  const { data, error } = await supabase
    .from('user_product_enrollments')
    .select(`
      product_id,
      is_active,
      products!inner(slug, name)
    `)
    .eq('user_id', userId)
    .eq('is_active', true);
  
  if (error) {
    return err(ApiError.fromSupabaseError(error));
  }
  
  // Transform to flat structure
  const enrollments: EnrollmentWithProduct[] = (data || []).map((item: any) => ({
    product_id: item.product_id,
    is_active: item.is_active,
    product_slug: item.products?.slug || item.product_id,
    product_name: item.products?.name || item.product_id,
  }));
  
  return ok(enrollments);
}

// ============ Queries ============

export async function getCurrentUser(): Promise<ApiResult<User>> {
  const { data: { user }, error } = await supabase.auth.getUser();
  
  if (error) {
    return err(ApiError.fromSupabaseError(error));
  }
  
  if (!user) {
    return err(ApiError.unauthorized());
  }
  
  return ok(user);
}

export async function getSession(): Promise<ApiResult<Session | null>> {
  const { data: { session }, error } = await supabase.auth.getSession();
  
  if (error) {
    return err(ApiError.fromSupabaseError(error));
  }
  
  return ok(session);
}

export async function checkIsAdmin(): Promise<ApiResult<boolean>> {
  const { data, error } = await supabase.rpc('verify_admin_access');
  
  if (error) {
    return err(ApiError.fromSupabaseError(error));
  }
  
  return ok(!!data);
}

export async function checkIsAdminByUserId(userId: string): Promise<ApiResult<boolean>> {
  const result = await supabase
    .from('user_roles')
    .select('role_type')
    .eq('user_id', userId)
    .in('role_type', ['platform_superadmin', 'brand_admin', 'group_admin', 'subgroup_admin'])
    .maybeSingle();

  if (result.error) {
    return err(ApiError.fromSupabaseError(result.error));
  }
  
  return ok(!!result.data);
}

/**
 * @deprecated Use is_platform_superadmin RPC instead
 * This function now calls is_platform_superadmin for backwards compatibility
 */
export async function checkIsSuperAdmin(): Promise<ApiResult<boolean>> {
  const { data, error } = await supabase.rpc('is_platform_superadmin');
  
  if (error) {
    return err(ApiError.fromSupabaseError(error));
  }
  
  return ok(!!data);
}

export interface AdminScope {
  is_super_admin: boolean;
  is_admin: boolean;
  is_brand_admin?: boolean;
  brand_id?: string | null;
  group_id: string | null;
  subgroup_id: string | null;
  role_type?: string;
}

export async function getAdminScope(): Promise<ApiResult<AdminScope>> {
  const { data, error } = await supabase.rpc('get_admin_scope');
  
  if (error) {
    return err(ApiError.fromSupabaseError(error));
  }
  
  // Function returns SETOF record (array), extract first element
  const scopeData = Array.isArray(data) ? data[0] : data;
  console.log('[getAdminScope] Raw data:', data, '| Extracted:', scopeData);
  
  return ok(scopeData as unknown as AdminScope);
}

export async function validateSignup(
  email: string,
  inviteCode?: string | null
): Promise<ApiResult<SignUpValidation>> {
  const { data, error } = await supabase.rpc('validate_signup', {
    p_email: email,
    p_invite_code: inviteCode ?? undefined,
  });

  if (error) {
    return err(ApiError.fromSupabaseError(error));
  }

  return ok(data as unknown as SignUpValidation);
}

// ============ Mutations ============

export async function signIn(
  email: string,
  password: string
): Promise<ApiResult<{ user: User; session: Session }>> {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });

  if (error) {
    return err(ApiError.fromSupabaseError(error));
  }

  if (!data.user || !data.session) {
    return err(new ApiError('Sign in failed', 'AUTH_ERROR'));
  }

  return ok({ user: data.user, session: data.session });
}

export async function signUp(
  email: string,
  password: string,
  metadata?: {
    signup_method?: string | null;
    signup_group_id?: string | null;
    signup_subgroup_id?: string | null;
    signup_invite_code?: string | null;
    signup_product_id?: string | null;
  },
  phone?: string // E.164 format: +15551234567
): Promise<ApiResult<{ user: User | null }>> {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    phone: phone || undefined, // Store in auth.users for SMS OTP login
    options: {
      emailRedirectTo: `${window.location.origin}/dashboard`,
      data: metadata,
    },
  });

  if (error) {
    return err(ApiError.fromSupabaseError(error));
  }

  // Enroll user in selected product (if product_id provided and user created)
  if (data.user && metadata?.signup_product_id) {
    // Enroll in product
    const { error: enrollError } = await supabase
      .from('user_product_enrollments')
      .insert({
        user_id: data.user.id,
        product_id: metadata.signup_product_id,
        is_active: true,
      });

    if (enrollError) {
      console.error('Failed to enroll user in product:', enrollError);
      // Don't fail signup if enrollment fails, the trigger will enroll in default product
    }

    // Update profile primary_product_id
    const { error: profileError } = await supabase
      .from('profiles')
      .update({ primary_product_id: metadata.signup_product_id })
      .eq('id', data.user.id);

    if (profileError) {
      console.error('Failed to update profile primary_product_id:', profileError);
    }
  }

  return ok({ user: data.user });
}

export async function markSignupUsed(
  email: string,
  inviteCode?: string | null
): Promise<ApiResult<null>> {
  const { error } = await supabase.rpc('mark_signup_used', {
    p_email: email,
    p_invite_code: inviteCode ?? undefined,
  });

  if (error) {
    return err(ApiError.fromSupabaseError(error));
  }

  return ok(null);
}

export async function updatePassword(newPassword: string): Promise<ApiResult<null>> {
  const { error } = await supabase.auth.updateUser({
    password: newPassword
  });

  if (error) {
    return err(ApiError.fromSupabaseError(error));
  }
  
  return ok(null);
}

export async function resetPasswordForEmail(email: string): Promise<ApiResult<null>> {
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/reset-password`,
  });

  if (error) {
    return err(ApiError.fromSupabaseError(error));
  }

  return ok(null);
}

export async function resendVerificationEmail(email: string): Promise<ApiResult<null>> {
  const { error } = await supabase.auth.resend({
    type: 'signup',
    email,
  });

  if (error) {
    return err(ApiError.fromSupabaseError(error));
  }

  return ok(null);
}

export async function signOut(): Promise<ApiResult<null>> {
  const { error } = await supabase.auth.signOut();

  if (error) {
    return err(ApiError.fromSupabaseError(error));
  }
  
  return ok(null);
}

export async function setSession(
  accessToken: string,
  refreshToken: string
): Promise<ApiResult<null>> {
  const { error } = await supabase.auth.setSession({ 
    access_token: accessToken, 
    refresh_token: refreshToken 
  });

  if (error) {
    return err(ApiError.fromSupabaseError(error));
  }
  
  return ok(null);
}

export async function exchangeCodeForSession(code: string): Promise<ApiResult<null>> {
  const { error } = await supabase.auth.exchangeCodeForSession(code);

  if (error) {
    return err(ApiError.fromSupabaseError(error));
  }
  
  return ok(null);
}

// ============ Subscriptions ============

export function onAuthStateChange(
  callback: (session: Session | null) => void
) {
  const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
    callback(session);
  });
  
  return subscription;
}

// Export supabase for realtime subscriptions (these must use direct client)
export { supabase } from '@/integrations/supabase/client';
